"use client"
import type { LegalContent } from "@/lib/legal"
import Link from "next/link"
import { ScrollReveal } from "@/components/scroll/ScrollReveal"
import { Footer } from "@/components/layout/Footer"
import { LegalNav } from "@/components/legal/LegalNav"

const BG = "#0B0F1C"
const ACCENT = "#C4541A"
const MUTED = "rgba(255,255,255,0.6)"
const BORDER = "rgba(255,255,255,0.08)"

// PAIA & POPIA Manual. Source manual corrected to the registered entity (CINNIMON t/a BoldPiq) and
// the Information Officer's residential address + personal phone numbers redacted for safety — PAIA
// only requires a reachable contact, not a home address. The full address is held privately for the
// Information Regulator submission. Prescribed request forms are summarised; the official forms are
// available on request / via the Information Regulator.

export function PaiaClient({ content }: { content: LegalContent }) {
  return (
    <main style={{ background: BG, color: "#fff", minHeight: "100vh" }}>
      <section style={{ padding: "clamp(100px, 14vw, 140px) clamp(20px, 4vw, 48px) clamp(60px, 8vw, 100px)", maxWidth: 860, margin: "0 auto" }}>
        <ScrollReveal effect="fade-up">
          <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 32 }}>
            <span style={{ width: 32, height: 1, background: ACCENT, flexShrink: 0 }} />
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED }}>Legal</span>
          </div>
          <h1 style={{ fontSize: "clamp(36px, 6vw, 80px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.9, marginBottom: 16 }}>{content.headingLine1}<br /><span style={{ color: ACCENT }}>{content.headingLine2}</span>
          </h1>
          <p style={{ color: MUTED, fontSize: 13, marginBottom: 8, opacity: 0.6 }}>{content.urlLine}</p>
          <p style={{ color: MUTED, fontSize: 14, marginBottom: 16 }}>{content.datesLine}</p>
          <p style={{ color: MUTED, fontSize: 14, marginBottom: 48, lineHeight: 1.65 }}>{content.intro}</p>
        </ScrollReveal>

        <LegalNav />

        <div style={{ display: "flex", flexDirection: "column", gap: 40 }}>
          {content.sections.map(section => (
            <div key={section.title} style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: 32 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 12 }}>{section.title}</h2>
              <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.75, whiteSpace: "pre-line" }}>{section.body}</p>
            </div>
          ))}

          <div style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 16 }}>External Resources</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <a href="https://inforegulator.org.za/" target="_blank" rel="noopener noreferrer"
                style={{ color: MUTED, fontSize: 14, textDecoration: "none" }}
              >
                → Information Regulator — South Africa
              </a>
              <a href="https://www.sahrc.org.za/" target="_blank" rel="noopener noreferrer"
                style={{ color: MUTED, fontSize: 14, textDecoration: "none" }}
              >
                → South African Human Rights Commission
              </a>
              <a href="/paia-manual.pdf" download
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 24px", background: ACCENT, color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: "none", letterSpacing: "0.06em", textTransform: "uppercase", width: "fit-content", marginTop: 8 }}
              >
                ↓ Download Manual (PDF)
              </a>
            </div>
          </div>

          <div style={{ paddingBottom: 16 }}>
            <p style={{ color: MUTED, fontSize: 14, lineHeight: 1.75 }}>
              See also:{" "}
              <Link href="/terms" style={{ color: ACCENT, textDecoration: "underline" }}>Terms of Service</Link>
              {" · "}
              <Link href="/privacy" style={{ color: ACCENT, textDecoration: "underline" }}>Privacy Policy</Link>
              {" · "}
              <Link href="/dpa" style={{ color: ACCENT, textDecoration: "underline" }}>Data Processing Agreement</Link>
              {" · "}
              <Link href="/refund" style={{ color: ACCENT, textDecoration: "underline" }}>Refund Policy</Link>
            </p>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  )
}
